import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { DetailedHTMLProps, FC, HTMLAttributes, ReactNode } from "react";

import { getTailwindLineClamp } from "../../../utils/tailwind/get-tailwind-line-clamp";
import { Cta } from "../../buttons/cta";
import { AppendixCardVariant } from "./types";

export interface AppendixCardProps
  extends Omit<Queries.AppendixCardDataFragment, "__typename">,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {
  light?: boolean;
  lineClamp?: number;
  shadow?: boolean;
  icon?: ReactNode;
}

export const AppendixCard: FC<AppendixCardProps> = ({
  title,
  description,
  image,
  cta,
  variant,
  className,
  light,
  lineClamp = 0,
  shadow,
  icon,
}) => {
  return (
    <div
      className={clsx([
        "transition-shadow",
        "flex flex-col gap-4 lg:gap-5 items-stretch",
        "rounded-3xl overflow-hidden pb-7 lg:pb-10",
        light ? "bg-neutral-light" : "bg-neutral",
        !image?.gatsbyImageData && "pt-7 lg:pt-10",
        className,
        shadow && "shadow-lg",
      ])}
    >
      {image?.gatsbyImageData && (
        <GatsbyImage
          image={image?.gatsbyImageData}
          alt={title ?? ""}
          objectFit="cover"
          className={clsx([
            variant === AppendixCardVariant.PADDED &&
              "h-52 lg:h-72 mt-5 lg:mt-10 mx-5 lg:mx-10 rounded-3xl",
            variant === AppendixCardVariant.FULL && "h-48 lg:h-80",
            variant === AppendixCardVariant.ICON &&
              "h-44 w-44 mt-5 lg:mt-10 mx-5 lg:mx-10 rounded-full self-center",
          ])}
        />
      )}
      {icon}
      {title && (
        <div
          className={clsx([
            "text-3xl text-primary leading-tight font-medium",
            "px-5 lg:px-10",
            variant === AppendixCardVariant.ICON ? "text-center" : "lg:text-4xl",
          ])}
        >
          {title}
        </div>
      )}
      {description?.text && (
        <div
          className={clsx([
            "text-base lg:text-lg leading-snug font-light",
            "px-5 lg:px-10",
            variant === AppendixCardVariant.ICON ? "text-center" : "lg:leading-normal",
            getTailwindLineClamp(lineClamp),
          ])}
        >
          {description.text}
        </div>
      )}
      {cta && (
        <div
          className={clsx(
            "!mt-auto",
            variant === AppendixCardVariant.ICON ? "text-center" : "px-5 lg:px-10 "
          )}
        >
          <Cta
            variant={variant === AppendixCardVariant.ICON ? "primary" : "primary-outline"}
            {...cta!}
            buttonClassName="max-w-full truncate"
          />
        </div>
      )}
    </div>
  );
};

export const query = graphql`
  fragment AppendixCardData on ContentfulAppendixCard {
    __typename
    title
    description {
      text: description
    }
    image {
      gatsbyImageData
    }
    cta {
      ...CtaData
    }
    variant
  }
`;
